import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "sonner";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import StickyMobileCTA from "@/components/StickyMobileCTA";
import Home from "@/pages/Home";
import OuverturePorte from "@/pages/OuverturePorte";
import Arrondissement from "@/pages/Arrondissement";
import ServicePage from "@/pages/ServicePage";
import Tarifs from "@/pages/Tarifs";
import Contact from "@/pages/Contact";
import Faq from "@/pages/Faq";
import MentionsLegales from "@/pages/MentionsLegales";
import Blog from "@/pages/Blog";
import Article from "@/pages/Article";
import ZonesIntervention from "@/pages/ZonesIntervention";
import SerrurierParis from "@/pages/SerrurierParis";
import SosSerrurier from "@/pages/SosSerrurier";
import SerrurierAutourDeMoi from "@/pages/SerrurierAutourDeMoi";
import BonSerrurier from "@/pages/BonSerrurier";
import Services from "@/pages/Services";
import Ville from "@/pages/Ville";
import DeptPage from "@/pages/DeptPage";
import ServiceDept from "@/pages/ServiceDept";
import InfoPage from "@/pages/InfoPage";
import NotFound from "@/pages/NotFound";
import { SERVICE_PAGES } from "@/data/services-pages";
import { INFO_PAGES } from "@/data/info-pages";
import { ARRONDISSEMENTS } from "@/data/site";
import { DEPTS, SERVICE_ZONES, VILLES } from "@/data/zones";

const REDIRECTS = [
    ["/serrurierparis", "/serrurier-paris"],
    ["/serrurier-paris-tarif", "/tarifs"],
    ["/sos-serrurier-paris", "/sos-serrurier"],
    ["/serrurier-autour-de-moi-paris", "/serrurier-autour-de-moi"],
    ["/serrurier-a-proximite", "/serrurier-autour-de-moi"],
    ["/ouverture-de-porte-paris", "/ouverture-de-porte"],
];

function ScrollToTop() {
    const { pathname } = useLocation();
    useEffect(() => {
        window.scrollTo(0, 0);
    }, [pathname]);
    return null;
}

function App() {
    useEffect(() => {
        const lenis = new Lenis({ duration: 1.1, smoothWheel: true });
        let rafId;
        const loop = (time) => {
            lenis.raf(time);
            rafId = requestAnimationFrame(loop);
        };
        rafId = requestAnimationFrame(loop);
        return () => {
            cancelAnimationFrame(rafId);
            lenis.destroy();
        };
    }, []);

    return (
        <div className="App bg-cloud font-sans text-midnight">
            <BrowserRouter basename={process.env.PUBLIC_URL}>
                <ScrollToTop />
                <Header />
                <main>
                    <Routes>
                        <Route path="/" element={<Home />} />
                        <Route path="/ouverture-de-porte" element={<OuverturePorte />} />
                        <Route path="/serrurier-paris" element={<SerrurierParis />} />
                        <Route path="/sos-serrurier" element={<SosSerrurier />} />
                        <Route path="/serrurier-autour-de-moi" element={<SerrurierAutourDeMoi />} />
                        <Route path="/bon-serrurier-paris" element={<BonSerrurier />} />
                        <Route path="/services" element={<Services />} />
                        <Route path="/zones-d-intervention" element={<ZonesIntervention />} />
                        {SERVICE_PAGES.map((s) => (
                            <Route key={s.slug} path={s.slug} element={<ServicePage />} />
                        ))}
                        {INFO_PAGES.map((p) => (
                            <Route key={p.slug} path={p.slug} element={<InfoPage />} />
                        ))}
                        {ARRONDISSEMENTS.map((a) => (
                            <Route key={a.n} path={`/serrurier-paris-${a.n}`} element={<Arrondissement numero={a.n} />} />
                        ))}
                        {ARRONDISSEMENTS.map((a) => (
                            <Route
                                key={`alias-${a.n}`}
                                path={`/serrurier-${a.n}e-arrondissement`}
                                element={<Navigate to={`/serrurier-paris-${a.n}`} replace />}
                            />
                        ))}
                        {VILLES.map((v) => (
                            <Route key={v.slug} path={`/serrurier-${v.slug}`} element={<Ville ville={v} />} />
                        ))}
                        {DEPTS.map((d) => (
                            <Route key={d.code} path={`/serrurier-${d.code}`} element={<DeptPage dept={d} />} />
                        ))}
                        <Route path="/serrurier-ile-de-france" element={<DeptPage idf />} />
                        {SERVICE_ZONES.map((z) => (
                            <Route key={`rideau-${z.slug}`} path={`/depannage-rideau-metallique-${z.slug}`} element={<ServiceDept type="rideau" zone={z} />} />
                        ))}
                        {SERVICE_ZONES.map((z) => (
                            <Route key={`volet-${z.slug}`} path={`/depannage-volet-roulant-${z.slug}`} element={<ServiceDept type="volet" zone={z} />} />
                        ))}
                        {REDIRECTS.map(([from, to]) => (
                            <Route key={from} path={from} element={<Navigate to={to} replace />} />
                        ))}
                        <Route path="/tarifs" element={<Tarifs />} />
                        <Route path="/devis-contact" element={<Contact />} />
                        <Route path="/faq" element={<Faq />} />
                        <Route path="/blog" element={<Blog />} />
                        <Route path="/blog/:slug" element={<Article />} />
                        <Route path="/mentions-legales" element={<MentionsLegales />} />
                        <Route path="*" element={<NotFound />} />
                    </Routes>
                </main>
                <Footer />
                <StickyMobileCTA />
                <Toaster position="top-center" richColors />
            </BrowserRouter>
        </div>
    );
}

export default App;
